/**
 * Fire-and-forget analytics.
 *
 * Every call site used to invoke `window.gtag(...)` directly, and roughly half did so unguarded.
 * That is a real hazard rather than a theoretical one, because `window.gtag` is not always there:
 *
 * - `src/tracking.js` defines it, and is loaded `async`. Until it executes, `window.gtag` is
 *   undefined — so a user who submits a form quickly can beat it.
 * - It is a first-party script named `tracking.js`. Content blockers (Brave Shields, Firefox
 *   Enhanced Tracking Protection, uBlock) target that name, and when they win the script never
 *   runs at all, so `window.gtag` is undefined for the whole session.
 *
 * Unguarded, `window.gtag(...)` then throws a TypeError. In `api.ts` that call sat *before*
 * `return await response.json()` in both `login` and `signup`, so a login the server had already
 * accepted would reject and surface as a failure. Analytics must never be able to fail a user
 * action, so nothing here throws and nothing returns a value worth checking.
 */
type EventPayload = Record<string, unknown>

export const trackEvent = (event: string, payload: EventPayload = {}) => {
    try {
        const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag
        if (typeof gtag !== 'function') return
        gtag('event', event, payload)
    } catch {
        // An analytics backend failing is never worth interrupting the user for.
    }
}

/** The shape almost every call site sends: an account-scoped event tagged with the username. */
export const trackAccountEvent = (event: string, username?: string, category = 'account') =>
    trackEvent(event, { event_category: category, event_label: username, user_id: username })
